import { STEPPER_GLOBAL_OPTIONS } from '@angular/cdk/stepper';
import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  importProvidersFrom,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import {
  MAT_PAGINATOR_DEFAULT_OPTIONS,
  MatPaginatorIntl,
} from '@angular/material/paginator';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  provideRouter,
  Routes,
  withComponentInputBinding,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { provideTransloco } from '@jsverse/transloco';
import { provideNgIdleKeepalive } from '@ng-idle/keepalive';
import { provideSqepIcons } from '@sqeptech/icons';
import { NgxEditorModule } from 'ngx-editor';
import { provideAuth } from './auth.providers';
import { AppConfig } from './config/app-config.token';
import {
  CARD_OPTIONS,
  DIALOG_OPTIONS,
  FORM_FIELD_OPTIONS,
  PAGINATOR_OPTIONS,
  SQEP_STEPPER_GLOBAL_OPTIONS,
} from './constants/constants.component';
import { DEV_TOOLS_PROVIDERS } from './dev-tools/dev-tools.providers';
import { TranslocoHttpLoader } from './transloco-loader';
import { PaginatorIntl } from './utilities/paginator-intl/paginator-util';

export type CoreOptions = {
  routes: Routes;
  config: AppConfig;
};

export function provideCore({ routes, config }: CoreOptions) {
  return [
    provideBrowserGlobalErrorListeners(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideAnimationsAsync(),

    provideSqepIcons(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
        // Angular 22 changed the default to 'always'; pin the pre-v22 behaviour
        // so child routes don't inherit all ancestor params (interacts with
        // withComponentInputBinding above). See router notes in CLAUDE.md.
        paramsInheritanceStrategy: 'emptyOnly',
      }),
      withInMemoryScrolling({
        anchorScrolling: 'enabled',
        scrollPositionRestoration: 'enabled',
      }),
    ),

    ...provideAuth(config),
    importProvidersFrom(
      NgxEditorModule.forRoot({
        locals: {
          enterValidUrl: 'Enter a valid URL (It must start with https://)',
        },
      }),
    ),
    provideNgIdleKeepalive(),
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    // Empty except under `ng serve` (development fileReplacements) — see dev-tools.providers.ts.
    ...DEV_TOOLS_PROVIDERS,
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { ...FORM_FIELD_OPTIONS },
    },
    {
      provide: MAT_CARD_CONFIG,
      useValue: {
        ...CARD_OPTIONS,
      },
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        ...DIALOG_OPTIONS,
      },
    },
    {
      provide: MAT_PAGINATOR_DEFAULT_OPTIONS,
      useValue: {
        ...PAGINATOR_OPTIONS,
      },
    },
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: {
        ...SQEP_STEPPER_GLOBAL_OPTIONS,
      },
    },
    { provide: MatPaginatorIntl, useClass: PaginatorIntl },

    // TO DO - MIGRATE INTERCEPTORS to Interceptors Functions
    // withInterceptors([
    //   oktaInterceptor,
    //   azureAllowedOrgInterceptor,
    //   authInterceptor,
    //   spinnerInterceptor,
    //   timeoutInterceptor,
    // ]),

    provideTransloco({
      loader: TranslocoHttpLoader,
      config: {
        availableLangs: [
          { id: 'en', label: 'English' },
          { id: 'es', label: 'Spanish' },
          { id: 'fr', label: 'French' },
          { id: 'ge', label: 'Germany' },
        ],
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        fallbackLang: 'en',
        defaultLang: 'en',
        missingHandler: {
          useFallbackTranslation: false,
        },
        prodMode: config.production,
      },
    }),
  ];
}
